import { IUserLog } from 'views/types/typeUser';
import { statusToText } from './status-to-text';

export const ENTITY_TYPE_MAP: Record<string, string> = {
  deposit_order: 'Deposit',
  withdraw_order: 'Withdraw',
  swap_order: 'Swap',
  card_transaction: 'Card Transaction',
  card_topup_order: 'Card Top-Up',
  card_withraw_order: 'Card Withdraw',
  card_issue_order: 'Card Issued',
  referral_reward: 'Referral Reward',
  referral_transaction_reward: 'Referral Reward',
  aml_check: 'AML Check',
  failed_kyc_verification: 'KYC Failed',
  success_kyc_verification: 'KYC Success',
  internal_deposit: 'Internal Deposit',
  internal_withdraw: 'Internal Withdrawal',
};

export const FIELD_TYPE_MAP: Record<string, string> = {
  last_active_at: 'Login',
  created_at: 'Account Created',
  referral_transaction_reward: 'Referral Reward',
  refferer_telegram_id: 'Referral Added',
  kyc_status: 'KYC',
};

export const fieldHandlers = {
  status: (log: IUserLog) => {
    if (log.entityType === 'CARD') {
      if (log.old_value === 'ACTIVE' && log.new_value === 'CLOSED') {
        return 'Blocked Card';
      }
      if (log.old_value === 'CLOSED' && log.new_value === 'ACTIVE') {
        return 'Unblocked Card';
      }
      return 'Card Status Change';
    }
    if (log.old_value === 'active' && log.new_value === 'blocked') {
      return 'Blocked';
    }
    if (log.old_value === 'blocked' && log.new_value === 'active') {
      return 'Unblocked';
    }
    return 'Status Change';
  },

  email: (log: IUserLog) => {
    return !log.old_value ? 'Email Added' : 'Email Changed';
  },

  is_two_factor_enabled: (log: IUserLog) => {
    const isEnabled = log.new_value === 'true' || log.new_value === '1';
    return isEnabled ? '2FA Enabled' : '2FA Disabled';
  },

  kyc_status: (log: IUserLog) => {
    return log.new_value === 'success' ? 'KYC Success' : 'KYC Failed';
  },

  balance: () => 'Balance Change by Admin',

  blocked_balance: () => 'Blocked Balance Change by Admin',
};

// Admin-set balance adjustments recorded as a per-user log entry
export const isBalanceLog = (log: IUserLog): boolean =>
  log.entityType === 'USER' &&
  (log.field === 'balance' || log.field === 'blocked_balance');

export const getOperationType = (log: IUserLog): string => {
  const entityType = log.entityType?.toLowerCase() || '';

  if (entityType === 'aml_check') {
    return log.comment;
  }

  if (
    entityType === 'referral_transaction_reward' &&
    log.field === 'created_at'
  ) {
    return 'Referral Transaction';
  }

  if (ENTITY_TYPE_MAP[entityType]) {
    return ENTITY_TYPE_MAP[entityType];
  }

  if (log.field && fieldHandlers[log.field as keyof typeof fieldHandlers]) {
    return fieldHandlers[log.field as keyof typeof fieldHandlers](log);
  }

  if (log.field && FIELD_TYPE_MAP[log.field]) {
    return FIELD_TYPE_MAP[log.field];
  }

  return log.comment || log.field || 'Unknown';
};

export const getStatus = (log: IUserLog): string => {
  if (log.status) {
    return statusToText(log.status, log.entityType) || log.status;
  }

  if (log.new_value) {
    const normalizedValue = statusToText(log.status, log.entityType);
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  const entityType = log.entityType?.toLowerCase() || '';
  const field = log.field?.toLowerCase() || '';

  if (
    entityType === 'user' &&
    ['last_active_at', 'created_at', 'email', 'is_two_factor_enabled'].includes(
      field
    )
  ) {
    return '--';
  }

  return '--';
};
